import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  StyleSheet,
  View,
  SafeAreaView,
  Platform,
  useWindowDimensions,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Card } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomDropDown from "../../../components/CustomDropDown/CustomDropdown";
import { useCompany } from "../../../hooks/useCompany";
import { useTranslation } from "react-i18next";
import CustomCalendar from "../../../components/CustomCalendar/CustomCalendar";
import {
  changeDateFromYYYYMMDDToDDMMYYYY,
  formatDateForUI,
} from "../../../constants/FormatDate";
import {
  useHRAdminEmployees,
} from "../../../hooks/useHRAdminEmployees";
import SalaryReviewScreen from "./SalaryReviewScreen";
import { useTableHelper } from "../../../hooks/useTableHelper";
import Swal from "sweetalert2";
import { showMessage } from "react-native-flash-message";

const NumberInput = ({ index, item, itemKey, handleEditPress }) => {
  const [number, setNumber] = useState(item[itemKey]);
  useEffect(() => {
    setNumber(item[itemKey]); // Update state when item changes
  }, [item[itemKey]]);

  return (
    <CustomInput
      value={number}
      setValue={(e) => {
        let onlyNum = e.replace(/[^0-9.]/g, ""); // allows numbers and dots
        let dotIndex = onlyNum.indexOf(".");
        if (dotIndex !== -1)
          onlyNum =
            onlyNum.slice(0, dotIndex + 1) +
            onlyNum.slice(dotIndex + 1).replace(/\./g, "");
        setNumber(onlyNum);
        handleEditPress(index, item, itemKey, onlyNum);
      }}
      maxLen={50}
      type="MAININPUT"
      keyboardType="numeric"
    />
  );
};

const SalaryReviewListView = React.memo(
  ({
    item,
    index,
    handleEditPress,
    currencies,
    salarySchemes,
    handleDeleteReview,
    styles,
    resetDates,
    handlePress,
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedData, setSelectedData] = useState({
      startDate: item.startDate || "",
      endDate: item.endDate || "",
    });
    const [rangeText, setRangeText] = useState("");
    const [localDescription, setLocalDescription] = useState(
      item.reviewDescription || ""
    );
    const [localComments, setLocalComments] = useState(item.comments || "");

    useEffect(() => {
      setLocalComments(item.comments || "");
    }, [item.comments]);

    useEffect(() => {
      setLocalDescription(item.reviewDescription || "");
    }, [item.reviewDescription]);

    useEffect(() => {
      if (rangeText === "" && item.startDate && item.endDate) {
        const from = changeDateFromYYYYMMDDToDDMMYYYY(item.startDate);
        const to = changeDateFromYYYYMMDDToDDMMYYYY(item.endDate);
        setRangeText(`${from} - ${to}`);
      }
    }, [item.startDate, item.endDate, rangeText]);

    const dateHelper = useCallback(() => {
      let dateArr = [];
      let tmpTdate = "";
      let tmpFdate = "";
      if (
        selectedData?.startDate &&
        selectedData?.startDate.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        tmpFdate = changeDateFromYYYYMMDDToDDMMYYYY(selectedData?.startDate);
      }
      if (
        selectedData?.endDate &&
        selectedData?.endDate.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        tmpTdate = changeDateFromYYYYMMDDToDDMMYYYY(selectedData?.endDate);
      }
      dateArr.push(tmpFdate);
      dateArr.push(tmpTdate);
      return dateArr;
    }, [selectedData]);

    const onShowCalendarPress = () => {
      let dateArr = [];
      if (showCalendar) {
        let dateArr = dateHelper();
        handleEditPress(index, item, "startDate - endDate", dateArr);
      }
      setShowCalendar(!showCalendar);
    };

    const handleDateChange = (newDates) => {
      // const today = new Date().toISOString().split("T")[0];
      setSelectedData(newDates);
      const safeStartDate = !newDates.startDate || newDates.startDate?.trim() === '' ? '' : newDates.startDate;
      const safeEndDate = !newDates.endDate || newDates.endDate?.trim() === '' ? '' : newDates.endDate;
      item.startDate = safeStartDate;
      item.endDate = safeEndDate;
      const dateArr = dateHelper();
      setRangeText(`${dateArr[0]} - ${dateArr[1]}`);
    };

    const toggleExpand = () => {
      setIsExpanded(!isExpanded);
    };

    useEffect(() => {
      if (resetDates) {
        // const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
        setSelectedData({
          startDate: item.startDate || "",
          endDate: item.endDate || "",
        });
        const from = changeDateFromYYYYMMDDToDDMMYYYY(item.startDate || "");
        const to = changeDateFromYYYYMMDDToDDMMYYYY(item.endDate || "");
        setRangeText(`${from} - ${to}`);
      }
    }, [resetDates, item.startDate, item.endDate]);

    return (
      <View>
        <SafeAreaView>
          <SafeAreaView style={styles.row}>
            <View style={[styles.item]}>
              <CustomInput
                value={localDescription}
                setValue={(text) => {
                  setLocalDescription(text);
                  handleEditPress(index, item, "reviewDescription", text);
                }}
                editable={true}
                maxLen={50}
                type="MAININPUT"
              />
            </View>
            <View style={[styles.item]}>
              <CustomDropDown
                defValue={item.salarySchemeId}
                options={salarySchemes}
                value={item.salarySchemeId}
                setValue={(e) =>
                  handleEditPress(index, item, "salarySchemeId", e)
                }
                onChange={(val) => {
                  setSelectedReview((prev) => ({
                    ...prev,
                    salarySchemeId: val,
                  }));
                }}
                type="FULL"
              />
            </View>
            <SafeAreaView style={styles.row}>
              <CustomInput
                value={formatDateForUI(selectedData.startDate)}
                setValue={() => { }}
                maxLen={50}
                editable={false}
                type="MAININPUT"
              />
              <Ionicons
                name={showCalendar ? "calendar" : "calendar-outline"}
                style={styles.icons}
                onPress={onShowCalendarPress}
              />
            </SafeAreaView>
            <SafeAreaView style={styles.row}>
              <CustomInput
                value={formatDateForUI(selectedData.endDate)}
                setValue={() => { }}
                type="MAININPUT"
                maxLen={50}
                editable={false}
              />
            </SafeAreaView>
            <SafeAreaView>
              <CustomButton
                iconName="trash-outline"
                onPress={() => handleDeleteReview(item, index)}
                show="HOME_BELL"
                type="HOME_BELL"
                textTop="2"
                sz={30}
              />
              <CustomButton
                iconName="wallet-outline"
                onPress={() => handlePress(item, index)}
                show="HOME_BELL"
                type="HOME_BELL"
                textTop="2"
                sz={30}
              />
              <Ionicons
                name={
                  isExpanded
                    ? "chevron-down-outline"
                    : "chevron-forward-outline"
                }
                style={styles.icons}
                onPress={toggleExpand}
              />
            </SafeAreaView>
          </SafeAreaView>
          {isExpanded && (
            <>
              <SafeAreaView style={styles.row2}>
                <View style={[styles.item, { justifyContent: "flex-start" }]}>
                  <CustomLabel value="Comments" type="SUBTITLE" />
                  <CustomInput
                    index={index}
                    value={localComments}
                    setValue={(text) => {
                      setLocalComments(text);
                      handleEditPress(index, item, "comments", text);
                    }}
                    type="MAININPUT"
                    editable={true}
                  />
                </View>
                <View style={[styles.itemCurrency]}>
                  <CustomLabel value="Currency" type="SUBTITLE" />
                  <CustomDropDown
                    defValue={item.currency}
                    options={currencies}
                    value={item.currency}
                    setValue={(e) =>
                      handleEditPress(index, item, "currency", e)
                    }
                    type="FULL"
                  />
                </View>
                <View style={[styles.item]}>
                  <CustomLabel value="Annual Gross Salary" type="SUBTITLE" />
                  <NumberInput
                    index={index}
                    item={item}
                    itemKey="annualGrossSalary"
                    handleEditPress={handleEditPress}
                  />
                </View>
                <View style={[styles.item]}>
                  <CustomLabel value="Monthly Gross Salary" type="SUBTITLE" />
                  <NumberInput
                    index={index}
                    item={item}
                    itemKey="monthlyGrossSalary"
                    handleEditPress={handleEditPress}
                  />
                </View>
              </SafeAreaView>
            </>
          )}
        </SafeAreaView>
        {showCalendar && (
          <SafeAreaView style={styles.secondLine}>
            <View
              style={{
                height:
                  Platform.OS === "web"
                    ? 285
                    : height < 670
                      ? (height * 40) / 100
                      : (height * 30) / 100,
                width: Platform.OS === "web" ? 485 : (width * 90) / 100,
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 10,
                borderWidth: 0,
                borderColor: "#e8e8e8",
              }}
            >
              <CustomCalendar
                calendarType={"RangePicker"}
                setRange={handleDateChange}
                range={selectedData}
              />
            </View>
          </SafeAreaView>
        )}
      </View>
    );
  }
);

const HRAdminSalaryReviews = (props) => {
  const { width, height } = useWindowDimensions();
  const { colors } = useTheme();
  const common = createCommonStyles(colors);
  const styles = makeStyles(colors, width);
  const [isLoading, setIsLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [salarySchemes, setSalarySchemes] = useState([]);
  const [salaryReviews, setSalaryReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [changes, setChanges] = useState({});
  const [deletedItems, setDeletedItems] = useState([]);
  const [insertData, setInsertData] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalData, setOriginalData] = useState([]);
  const { rowAdd, rowEdit } = useTableHelper();
  const [duplicates, setDuplicates] = useState([]);
  const { t } = useTranslation();
  const ref = useRef(null);
  const { getSalaryReviewById, deleteSalaryReview, saveSalaryReview } =
    useHRAdminEmployees();
  const { getCurrencies, getSalarySchemes } = useCompany();
  const [currencies, setCurrencies] = useState([]);
  const [showSalaryReviewScreen, setShowSalaryReviewScreen] = useState(false);
  const [resetDates, setResetDates] = useState([false]);

  const showWarningPopup = async () => {
    return Swal.fire({
      title: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
        "common:changeSchemeMessage"
      )}</text>`,
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
        "common:cancel"
      )}</text>`,
      cancelButtonColor: "#d3d3d3",
      confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
        "common:confirmMessage"
      )}</text>`,
      confirmButtonColor: "#A0A2FF",
    });
  };

  useEffect(() => {
    setIsLoading(true);
    fetchSalaryReviews();
  }, [props.selectedEmployee, props.selectedCompany]);

  const fetchSalaryReviews = async () => {
    try {
      const empls = await getSalaryReviewById(
        props?.selectedEmployee?.employeeID
      );
      const currencies = await getCurrencies();
      const res = await getSalarySchemes(props?.selectedCompany?.code);

      let r = res.map((item) => ({
        value: item.salarySchemeId,
        label: item.desc,
      }));

      setSalarySchemes(r);
      setCurrencies(currencies);
      empls.forEach((obj) => {
        obj.startDate = obj.startDate?.split("T")[0];
        obj.endDate = obj.endDate?.split("T")[0];
      });

      setSalaryReviews(empls);
      setOriginalData(empls);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching salary reviews:", error);
      setIsLoading(false);
    }
  };
  useEffect(() => { }, [salaryReviews]);

  const handleEditPress = useCallback(
    async (index, item, key, value) => {
      // Check if the value has changed
      if (item[key] === value) return;
      // Store the original value for potential rollback
      const originalValue = item[key];
      // If the key is 'salarySchemeId' and the item is not new, show a warning popup
      if (
        key === "salarySchemeId" &&
        item.salarySchemeId && // there was a previous value
        item.salarySchemeId !== value // and it's being changed
      ) {
        const result = await showWarningPopup();
        if (!result.isConfirmed) {
          // Revert to the original value if the user cancels the change
          rowEdit(
            index,
            item,
            key,
            originalValue,
            changes,
            setChanges,
            salaryReviews,
            setSalaryReviews
          );
          return;
        }
      }
      // Use rowEdit to apply the change
      rowEdit(
        index,
        item,
        key,
        value,
        changes,
        setChanges,
        salaryReviews,
        setSalaryReviews
      );
    },
    [showWarningPopup, rowEdit, changes, salaryReviews]
  );

  const handleCancelChanges = () => {
    const rolledBackReviews = originalData.map((obj) => ({ ...obj }));
    setSalaryReviews(rolledBackReviews);
    setDeletedItems([]);
    setChanges({});
    setDuplicates([]);
    setHasUnsavedChanges(false);
    setInsertData([]);
    showMessage({
      message: t("common:changesCancelled"),
      type: "info",
      duration: 3000,
    });
    setResetDates(true);
  };

  const handleSaveReview = async () => {
    try {
      // Show validation: Ensure salary scheme is selected
      if (
        !salaryReviews ||
        salaryReviews.some((review) => !review.salarySchemeId || review.salarySchemeId === '00000000-0000-0000-0000-000000000000')
      ) {
        showMessage({
          message: t("common:salarySchemeMustBeSelected"),
          type: "danger",
          duration: 3000,
        });
        return;
      }
      // Check if salaryReviews has more than one item
      if (salaryReviews.length > 1) {
        // Count how many items have missing or empty endDate
        const missingEndDateCount = salaryReviews.filter(
          (review) => !review.endDate || review.endDate.trim() === ""
        ).length;

        // If more than one item is missing endDate, show error
        if (missingEndDateCount > 1) {
          showMessage({
            message: t("common:endDateMustBeSelected"),
            type: "danger",
            duration: 3000,
          });
          return;
        }
      }

      // convert empty startdate/enddate strings to null (to be parsed correctly by API)
      function convertEmptyDatesToNull(salaryReviews) {
        return salaryReviews.map(review => ({
          ...review,
          startDate: (review.startDate === "" || review.startDate == 0) ? null : review.startDate,
          endDate: (review.endDate === "" || review.endDate == 0) ? null : review.endDate,
        }));
      }
      const updatedReviews = convertEmptyDatesToNull(salaryReviews);

      let savePromises = [];
      let updateSucc = true;
      let insertSucc = true;
      // === SAVE NEW/UPDATED ROWS ===
      savePromises.push(
        saveSalaryReview(
          updatedReviews,
          props?.selectedEmployee?.employeeID
        ).catch((error) => {
          console.error("Save error", error);
          updateSucc = false;
        })
      );
      // === DELETE ROWS MARKED FOR DELETION ===
      for (const item of deletedItems) {
        savePromises.push(
          deleteSalaryReview(`'${item.emplSalaryId}'`).catch((error) => {
            insertSucc = false;
          })
        );
      }
      // Wait for all promises to resolve
      await Promise.all(savePromises);
      // === Handle results ===
      if (!updateSucc || !insertSucc) {
        showMessage({
          message: t("common:errorSavingReview"),
          type: "danger",
          duration: 3000,
        });
        return;
      }
      // === Fetch updated list from backend ===
      const empls = await getSalaryReviewById(
        props?.selectedEmployee?.employeeID
      );
      // === Update UI ===
      setSalaryReviews(empls);
      // === FIX: Update the cancel snapshot (originalData) ===
      // Deep clone to avoid reference issues
      setOriginalData(
        typeof structuredClone === "function"
          ? structuredClone(empls)
          : JSON.parse(JSON.stringify(empls))
      );
      // === Reset all change trackers ===
      setDeletedItems([]);
      setInsertData([]);
      setChanges({});
      setHasUnsavedChanges(false);
      // === Reset any date flags or temporary states ===
      setResetDates(true);
      // === Success message ===
      showMessage({
        message: t("common:reviewSavedSuccessfully"),
        type: "success",
        duration: 3000,
      });
      // Remove changes from diff tracking
      removeFromChangesAfterSave();
    } catch (error) {
      console.error("Error during save:", error);
      showMessage({
        message: t("common:errorSavingReview"),
        type: "danger",
        duration: 3000,
      });
    }
  };

  const handleDeleteReview = (item, index) => {
    // Add the item to the list of deleted items
    setDeletedItems((prevDeletedItems) => [...prevDeletedItems, item]);
    // Remove the item from the frontend list
    setSalaryReviews((prevReviews) =>
      prevReviews.filter((review, i) => i !== index)
    );
    // Optionally, show a message indicating the item was removed from the frontend
    showMessage({
      message: t("common:reviewMarkedForDeletion"),
      type: "info",
      duration: 3000,
    });
  };

  const rowDelete = (
    item,
    index,
    attribute,
    setReviews,
    setUnsavedChanges,
    showMessage,
    t
  ) => {
    setReviews((prevReviews) => prevReviews.filter((review, i) => i !== index));
    setUnsavedChanges(false);
    showMessage({
      message: t("common:reviewDeletedSuccessfully"),
      type: "success",
      duration: 3000,
    });
  };

  const handlePress = (item, index) => {
    setIsTransitioning(true);
    setSelectedReview(item);
    setTimeout(() => {
      setShowSalaryReviewScreen(true);
      setIsTransitioning(false); // End transition loading
    }, 500);
  };

  const handleGoBack = () => {
    setShowSalaryReviewScreen(false);
  };

  const removeFromChangesAfterSave = () => {
    setChanges([]);
    setDeletedItems([]);
  };

  const renderSalaryReviews = useCallback(
    ({ item, index }) => (
      <Card style={[common.cardstyle]}>
        <Card.Content>
          <SalaryReviewListView
            item={item}
            index={index}
            handleEditPress={handleEditPress}
            currencies={currencies}
            salarySchemes={salarySchemes}
            handleDeleteReview={handleDeleteReview}
            handlePress={handlePress}
            styles={styles}
            resetDates={resetDates}
          />
        </Card.Content>
      </Card>
    ),
    [
      salaryReviews,
      resetDates,
      handleEditPress,
      currencies,
      salarySchemes,
      handleDeleteReview,
      handlePress,
      styles,
    ]
  );

  if (isLoading || isTransitioning) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator animating={true} color={colors.blue} />
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.mainView}>
      {showSalaryReviewScreen ? (
        // Conditionally render the SalaryReviewScreen
        <SafeAreaView style={styles.salaryReviewScreenView}>
          <TouchableOpacity onPress={handleGoBack} style={styles.backButton}>
            <Ionicons name="arrow-back" size={30} color="black" />
          </TouchableOpacity>
          <SalaryReviewScreen
            selectedReview={selectedReview}
            selectedEmployee={props.selectedEmployee}
          />
        </SafeAreaView>
      ) : (
        // Render the list of salary reviews
        <View style={styles.container}>
          <View style={[styles.buttons, styles.functionalityButtons]}>
            <CustomButton
              text={t("common:new")}
              onPress={() => {
                rowAdd(
                  salaryReviews,
                  setSalaryReviews,
                  ["emplsalaryid"],
                  [],
                  ref,
                  t,
                  [],
                  [
                    "monthlyGrossSalary",
                    "annualGrossSalary",
                    "startDate",
                    "endDate",
                  ],
                  changes,
                  setChanges,
                  undefined,
                  undefined,
                  "emplSalaryId",
                  false
                );
              }}
              type="BUTTON_ADD"
              textTop="0"
              sz={15}
            />
            {(Object.keys(changes).length > 0 ||
              changes.length > 0 || deletedItems.length > 0) && (
                <CustomButton
                  text={t("common:saveFunction")}
                  onPress={handleSaveReview}
                  type="BUTTON_SAVE"
                  textTop="0"
                  sz={15}
                />
              )}
            <CustomButton
              text={t("common:cancel")}
              onPress={handleCancelChanges}
              type="BUTTON_CANCEL"
              textTop="0"
              sz={15}
            />
          </View>
          <SafeAreaView style={common.divider} />
          <FlatList
            data={salaryReviews}
            keyExtractor={(item, index) => `${item.EmplSalaryId}-${index}`}
            renderItem={renderSalaryReviews} // Use the memoized function here
            refreshing={isLoading}
            ref={ref}
            ListHeaderComponent={
              <SafeAreaView>
                <View style={[styles.row, { marginRight: "3%" }]}>
                  <CustomLabel
                    value={t("common:reviewDescription")}
                    type="SUBTITLE"
                  />
                  <CustomLabel
                    value={t("common:schemeDescription")}
                    type="SUBTITLE"
                  />
                  <CustomLabel value={t("common:startDate")} type="SUBTITLE" />
                  <SafeAreaView />
                  <CustomLabel value={t("common:endDate")} type="SUBTITLE" />
                </View>
              </SafeAreaView>
            }
            ListEmptyComponent={
              isLoading ? (
                <ActivityIndicator animating={true} color={colors.blue} />
              ) : (
                <CustomLabel
                  value={t("common:noReviewDataPreview")}
                  type="NO_FUNCTIONS"
                />
              )
            }
            initialNumToRender={4}
          />

        </View>
      )}
    </SafeAreaView>
  );
};

const makeStyles = (colors, width) =>
  StyleSheet.create({
    mainView: {
      flex: 1,
    },
    rowView: {
      flex: 1,
      flexDirection: "row",
      justifyContent: "center",
    },
    container: {
      flex: 1,
    },
    row: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
    },
    row2: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      marginRight: 22
    },
    item: {
      flex: 1,
      justifyContent: "center",
      marginRight: 6
    },
    itemCurrency: {
      flex: 1,
      justifyContent: "center",
      marginRight: 6,
      flexDirection: "column",
      gap: 10
    },
    buttons: {
      flexDirection: "row",
      justifyContent: "flex-end",
      alignSelf: "flex-end",
      marginRight: 15,
    },
    functionalityButtons: {
      justifyContent: "flex-end",
    },
    loader: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "4%",
    },
    icons: {
      fontSize: 24,
      marginHorizontal: 10,
    },
    backButton: {
      flexDirection: "row",
      justifyContent: "flex-end",
    },
  });

export default HRAdminSalaryReviews;
